import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import axiosInstance from '../axiosInstance';
import { useAuth } from '../context/AuthContext';
import themeAssure from './theme';

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

/**
 * La page qu'ouvre l'invité : relier son dossier à celui de l'invitant.
 *
 * Il faut un compte, à l'adresse invitée. L'invité choisit un de ses dossiers,
 * ou en ouvre un, prérempli avec la date et l'adresse du sinistre.
 */
export default function PageLiaison() {
  const { jeton } = useParams();
  const naviguer = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [erreur, setErreur] = useState('');
  const [choix, setChoix] = useState('nouveau');
  const [reference, setReference] = useState('');
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    axiosInstance.get(`/liaisons/invitations/${jeton}/`)
      .then(({ data }) => setInvitation(data))
      .catch((e) => setErreur(e?.response?.data?.detail
        || 'Impossible de contacter le serveur.'));
  }, [jeton, isAuthenticated]);

  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to={`/login?suite=${encodeURIComponent(`/relier/${jeton}`)}`} replace />;
  }

  const accepter = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const { data } = await axiosInstance.post(`/liaisons/invitations/${jeton}/`, {
        dossier: choix === 'nouveau' ? null : Number(choix),
        reference,
      });
      naviguer(data.chemin);
    } catch (e) {
      setErreur(e?.response?.data?.detail || "La relation n'a pas pu être créée.");
      setOccupe(false);
    }
  };

  const utilisable = invitation && invitation.statut === 'en attente' && !invitation.refus;

  return (
    <ThemeProvider theme={themeAssure}>
      <ScopedCssBaseline>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
          <Container maxWidth="sm">
            <Typography variant="h1" sx={{ mb: 1 }}>Relier votre dossier</Typography>

            {erreur && <Alert severity="warning" sx={{ mb: 3 }}>{erreur}</Alert>}
            {!invitation && !erreur && <CircularProgress size={28} />}

            {invitation && (
              <Stack spacing={3}>
                <Typography variant="body1">
                  {invitation.invitant || invitation.organisation}
                  {invitation.invitant ? ` (${invitation.organisation})` : ''} vous
                  propose de relier votre dossier au sien. Chacun garde son propre
                  dossier : vous n'échangerez que ce que vous vous enverrez.
                </Typography>

                {invitation.statut !== 'en attente' && (
                  <Alert severity="info">Cette invitation est {invitation.statut}.</Alert>
                )}
                {invitation.statut === 'en attente' && invitation.refus && (
                  <Alert severity="warning">{invitation.refus}</Alert>
                )}

                {utilisable && (
                  <>
                    <Paper variant="outlined" sx={{ p: 2.5 }}>
                      <Typography variant="body2" color="text.secondary">Le sinistre</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {invitation.reference ? `Dossier ${invitation.reference}` : 'Sans référence'}
                      </Typography>
                      <Typography variant="body2">
                        {[invitation.date_sinistre && `Survenu le ${dateCourte(invitation.date_sinistre)}`,
                          invitation.adresse_sinistre].filter(Boolean).join(' · ')
                          || "Ni date ni adresse renseignées."}
                      </Typography>
                    </Paper>

                    <Box>
                      <Typography variant="h3" sx={{ mb: 1 }}>Quel est votre dossier ?</Typography>
                      <RadioGroup value={choix} onChange={(e) => setChoix(e.target.value)}>
                        <FormControlLabel value="nouveau" control={<Radio />}
                                          label="Ouvrir un dossier pour ce sinistre" />
                        {invitation.dossiers.map((d) => (
                          <FormControlLabel
                            key={d.id} value={String(d.id)} control={<Radio />}
                            label={(
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {d.reference ? `${d.reference} — ` : ''}{d.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {[dateCourte(d.date_sinistre), d.adresse_sinistre]
                                    .filter(Boolean).join(' · ')}
                                </Typography>
                              </Box>
                            )} />
                        ))}
                      </RadioGroup>
                    </Box>

                    <TextField
                      label="Votre référence pour ce dossier"
                      value={reference} onChange={(e) => setReference(e.target.value)}
                      helperText={choix === 'nouveau'
                        ? "Le dossier reprendra la date et l'adresse du sinistre, rien d'autre."
                        : "Facultatif. L'autre partie verra votre référence et le nom de votre organisation."} />

                    <Button variant="contained" size="large" onClick={accepter}
                            disabled={occupe}>
                      {occupe ? 'Un instant…' : 'Relier les dossiers'}
                    </Button>
                  </>
                )}

                <Button component={Link} to="/" color="inherit">
                  Aller à mon espace
                </Button>
              </Stack>
            )}
          </Container>
        </Box>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
